import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

const StorageEntrySchema = z.object({
  value: z.unknown(),
  version: z.string(),
})

const readFromStorage = <T>(key: string, version: string, initialValue: T): T => {
  try {
    const item = localStorage.getItem(key)
    if (!item) return initialValue

    const parsed: unknown = JSON.parse(item)
    const entry = StorageEntrySchema.parse(parsed)

    if (entry.version !== version) return initialValue

    return entry.value as T
  } catch {
    localStorage.removeItem(key)
    console.error('Error reading from local storage: ', key)
    return initialValue
  }
}

export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  version = 'v1',
): [T, (value: T) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() =>
    readFromStorage(key, version, initialValue),
  )

  const setValue = useCallback(
    (value: T) => {
      setStoredValue(value)
      localStorage.setItem(key, JSON.stringify({ value, version }))
    },
    [key, version],
  )

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) return

      if (event.newValue == null) {
        setStoredValue(initialValue)
        localStorage.removeItem(key)
        return
      }

      try {
        const parsed: unknown = JSON.parse(event.newValue)
        const entry = StorageEntrySchema.parse(parsed)

        if (entry.version !== version) {
          setStoredValue(initialValue)
          return
        }

        setStoredValue(entry.value as T)
      } catch {
        setStoredValue(initialValue)
        localStorage.removeItem(key)
        console.error('Error reading from local storage: ', key)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [key, version, initialValue])

  return [storedValue, setValue]
}
